import type { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useCloseOpenShiftSuggestion } from "../../contexts/CloseOpenShiftSuggestionProvider";
import { useDraftMode } from "../../contexts/DraftModeContext";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useErrorSnackbar } from "../../hooks/useErrorSnackbar";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { useJobs } from "../../hooks/useJobs";
import { useNurseTimeOff } from "../../hooks/useNurseTimeOff";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { usePrepareNurseShift } from "../../hooks/usePrepareNurseShift";
import { useScheduleSaves } from "../../hooks/useScheduleSaves";
import { scheduleSlotDay } from "../../utils/departmentWeeks";
import { type Nurse, type NurseData, type NurseInShift, NurseInShiftCreationSource, type OpenShiftRequest, type WeekData } from "../../utils/models";
import type { ShiftAvailabilityWindow } from "../../utils/shiftAvailability";
import { classifyAddWarnings } from "../../utils/warningDialogs/classifyAddWarnings";
import { useStaffingTargetCheck } from "../../utils/warningDialogs/useStaffingTargetCheck";
import { DIFF_HOURS_WARNING } from "../../utils/warningDialogs/utils";
import { useNurseWarnings } from "../../utils/warningDialogs/WarningDialog";
import { usePayrollConfirmation } from "../hooks/usePayrollConfirmation";
import type { AddStaffDialogsProps } from "./AddStaffDialogs";
import {
	ADD_STAFF_ANALYTICS_SOURCE,
	buildAddNurseEventProps,
	buildPreparedShift,
	findRedundantOpenShiftFor,
	shouldOfferRedundantOpenShift,
	toAddStaffTarget,
} from "./addStaffPublish";
import { type ShiftAssignment, SINGLE_ADD_COUNT } from "./addStaffUtils";
import { type MultiAddRequest, type MultiAddSubmit, useMultiAddSubmit } from "./useMultiAddSubmit";

type NurseChange = ReturnType<typeof useHandleNurseChange>;

/** A warning `classifyAddWarnings` ruled out, as the cascade takes it: zero hours, nothing to confirm. */
const NO_HOURS = 0;

export type AddStaffSubmitArgs = {
	/** The picked staff, in pick order. */
	selectedNurses: Nurse[];
	/** The step-2 rows — the add reads each pick's predicted overtime and daily hours from them. */
	rows: NurseData[];
	/** The rows' cost / overtime are still the previous window's (or not loaded yet): the add waits for them. */
	arePredictionsPending: boolean;
	window: ShiftAvailabilityWindow | null;
	staffingDate: DateTime;
	/** The panel's schedule weeks; `weekIndex`, `dayNo` and `shiftNo` are positions in them. */
	weeks: WeekData[];
	shiftNo: number;
	dayNo: number;
	weekIndex: number;
	room: string;
	jobId: string | undefined;
	assignment: ShiftAssignment;
	showActual: boolean;
	hideRoomSelect: boolean;
	onNurseSelected: ((nurse: Nurse, selectedDate: DateTime | null) => void) | undefined;
	onNurseAddedAvoidActualAdding: ((selectedNurseId: number) => void) | undefined;
	setIsAdd: (value: boolean) => void;
	close: () => void;
	skipRedundantOpenShiftSuggestion: boolean;
};

export type AddStaffSubmit = {
	/** The confirm button: one pick → today's single-add path; several → the multi-add (one publish). */
	requestAdd: () => void;
	/** Whether the confirm button can fire for the current picks. */
	canSubmit: boolean;
	isSubmitting: boolean;
	/** Picks are held but this window's predicted cost / overtime haven't arrived, so the add waits on them. */
	isAwaitingCost: boolean;
	dialogs: AddStaffDialogsProps;
};

/**
 * Callers that take the pick themselves handle exactly one nurse, and the multi-add path doesn't run their
 * callbacks — so several picks can't submit there: the hospital flow (`hideRoomSelect`, the only case where
 * `onNurseSelected` is ever called) and a replace-request fill (`onNurseAddedAvoidActualAdding`). Their
 * callers already pick in single-select mode; this keeps it true if that ever drifts.
 */
export const takesSinglePick = (args: Pick<AddStaffSubmitArgs, "hideRoomSelect" | "onNurseAddedAvoidActualAdding">): boolean =>
	args.hideRoomSelect || !!args.onNurseAddedAvoidActualAdding;

/**
 * The single add's warning cascade (employee status, staffing target, PTO, daily hours, overtime). WHICH
 * warnings apply is `classifyAddWarnings` — the multi-add review's own rule — and the cascade only orders
 * them, so a warning that doesn't apply goes in as nothing to confirm (0 h).
 */
const useSingleAddWarnings = (args: AddStaffSubmitArgs, onAllClear: () => void) => {
	const { chosenDepartment } = useChosenDepartment();
	const getTargetCheck = useStaffingTargetCheck();
	const timeOffOf = useNurseTimeOff();
	const [warningState, checkWarnings, ...warningHandlers] = useNurseWarnings(onAllClear, chosenDepartment.longShiftDuration);
	const { rows, room, weeks, weekIndex, dayNo, shiftNo, showActual, staffingDate } = args;

	const run = (nurse: Nurse, window: ShiftAvailabilityWindow, withTargetCheck: boolean) => {
		const warnings = classifyAddWarnings(
			nurse,
			rows.find((candidate) => candidate.id === nurse.id),
			{
				window,
				dayStart: chosenDepartment.dayStart,
				timeOffOf,
				departmentId: chosenDepartment.department_id,
				staffingDate,
				isActual: showActual,
				// DEFAULT: the long-day limit useNurseWarnings itself falls back to, so classifier and cascade agree.
				longShiftLimitHours: chosenDepartment.longShiftDuration ?? DIFF_HOURS_WARNING,
			},
		);
		const targetWarning = withTargetCheck
			? getTargetCheck({ weeks, locationName: room, weekIndex, dayNo, shiftIndex: shiftNo, isActual: showActual })
			: undefined;
		const { isAgency, overtime, longDayHours, pto, employeeStatus } = warnings;
		checkWarnings(isAgency, overtime?.hours ?? NO_HOURS, overtime?.cost, longDayHours ?? NO_HOURS, showActual, pto, targetWarning, employeeStatus);
	};
	return { warningState, warningHandlers, run };
};

/**
 * Actuals mode asks "this changes actual working hours" before anything else. Confirming it stands in for
 * the single add's staffing-target step, exactly as before (it only shows for actuals, where the target
 * never applies — the multi review skips it the same way).
 */
const usePayrollGate = (showActual: boolean, proceed: (withTargetCheck: boolean) => void) => {
	const needsPayrollConfirmation = usePayrollConfirmation(showActual);
	const [open, setOpen] = useState(false);
	return {
		open,
		request: () => (needsPayrollConfirmation ? setOpen(true) : proceed(true)),
		cancel: () => setOpen(false),
		confirm: () => {
			setOpen(false);
			proceed(false);
		},
	};
};

/**
 * The confirm button, both ways. One pick runs the single add step for step as it has always run:
 * payroll confirmation (actuals) → the warning cascade → publish → local copy → snackbar → the
 * redundant-open-shift offer. Several picks run the same payroll confirmation once, then
 * `useMultiAddSubmit`'s combined review and single publish.
 */
export const useAddStaffSubmit = (args: AddStaffSubmitArgs): AddStaffSubmit => {
	const { selectedNurses, window } = args;
	const { chosenDepartment } = useChosenDepartment();
	// ONE mutation owner for the whole panel: `isLoading` must track the very mutation the add runs.
	const nurseChange = useHandleNurseChange();
	const multiAdd = useMultiAddSubmit(nurseChange);
	const count = selectedNurses.length;
	const singleNurse = count === SINGLE_ADD_COUNT ? selectedNurses[0] : null;
	const addSingle = useSingleAdd(args, singleNurse, nurseChange.addNurse);
	const warnings = useSingleAddWarnings(args, () => void addSingle());

	const isMulti = count > SINGLE_ADD_COUNT;
	const isSubmitting = nurseChange.isLoading.addNurse || multiAdd.isSubmitting;
	// The warnings and the cost read the rows' predictions: adding before THIS window's arrive would confirm
	// (or skip) overtime and show a cost from the previously picked shift.
	const isAwaitingCost = count > 0 && args.arePredictionsPending;
	const canSubmit = !!window && !isSubmitting && !isAwaitingCost && (count === SINGLE_ADD_COUNT || (isMulti && !takesSinglePick(args)));

	// After the payroll confirmation (or straight away without one): the review / cascade for the picks.
	const proceed = (withTargetCheck: boolean) => {
		if (!window) return;
		if (isMulti) void multiAdd.submit(toMultiAddRequest(args, window));
		else if (singleNurse) warnings.run(singleNurse, window, withTargetCheck);
	};
	const payroll = usePayrollGate(args.showActual, proceed);

	const dialogs: AddStaffDialogsProps = {
		warningState: warnings.warningState,
		warningHandlers: warnings.warningHandlers,
		longShiftDuration: chosenDepartment.longShiftDuration,
		payrollConfirmOpen: payroll.open,
		onPayrollCancel: payroll.cancel,
		onPayrollConfirm: payroll.confirm,
		multiAddReview: toMultiAddReviewProps(multiAdd, !args.hideRoomSelect),
	};
	const requestAdd = () => {
		if (canSubmit) payroll.request();
	};
	return { requestAdd, canSubmit, isSubmitting, isAwaitingCost, dialogs };
};

const toMultiAddReviewProps = (multiAdd: MultiAddSubmit, showLocation: boolean): AddStaffDialogsProps["multiAddReview"] => ({
	pending: multiAdd.pending,
	showLocation,
	isSubmitting: multiAdd.isSubmitting,
	onBack: multiAdd.cancelReview,
	onConfirm: multiAdd.confirmReview,
});

const toMultiAddRequest = (args: AddStaffSubmitArgs, window: ShiftAvailabilityWindow): MultiAddRequest => ({
	nurses: args.selectedNurses,
	rows: args.rows.filter((row) => args.selectedNurses.some((nurse) => nurse.id === row.id)),
	window,
	staffingDate: args.staffingDate,
	weeks: args.weeks,
	shiftNo: args.shiftNo,
	dayNo: args.dayNo,
	weekIndex: args.weekIndex,
	room: args.room,
	jobId: args.jobId,
	assignment: args.assignment,
	isActual: args.showActual,
	skipRedundantOpenShiftSuggestion: args.skipRedundantOpenShiftSuggestion,
	setIsAdd: args.setIsAdd,
	close: args.close,
});

/** The open shift this add would make redundant — resolved BEFORE the add, since adding refreshes the requests it reads. */
const useRedundantOpenShift = (args: AddStaffSubmitArgs, nurse: Nurse | null): OpenShiftRequest | undefined => {
	const { chosenDepartment } = useChosenDepartment();
	const { isHospital } = useDepartmentFlags();
	const { isDraftMode } = useDraftMode();
	const { staffRequests } = useRequestsContext();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { skipRedundantOpenShiftSuggestion, showActual, window, room, jobId, assignment, weekIndex, dayNo, shiftNo } = args;
	return useMemo(() => {
		if (!shouldOfferRedundantOpenShift({ skipRedundantOpenShiftSuggestion, isActual: showActual, isDraftMode, isHospital })) return undefined;
		if (!window || !nurse) return undefined;
		const target = toAddStaffTarget({ weekIndex, dayNo, shiftNo, room, jobId, assignment }, window);
		return findRedundantOpenShiftFor(staffRequests, target, nurse, { departmentId: chosenDepartment.department_id, jobManagementEnabled });
	}, [
		skipRedundantOpenShiftSuggestion,
		showActual,
		isDraftMode,
		isHospital,
		window,
		nurse,
		staffRequests,
		chosenDepartment,
		room,
		jobId,
		jobManagementEnabled,
		assignment,
		weekIndex,
		dayNo,
		shiftNo,
	]);
};

/** The shift record for the add: the grid's prepared nurse with the picked hours, details and job. */
const usePreparedShift = (args: AddStaffSubmitArgs, nurse: Nurse | null): (() => NurseInShift | undefined) => {
	const prepareNurseShift = usePrepareNurseShift();
	const enabledJobs = useJobs();
	return () => {
		if (!nurse || !args.window) return undefined;
		const deps = { weeks: args.weeks, prepareNurseShift, enabledJobs };
		return buildPreparedShift(deps, toAddStaffTarget(args, args.window), nurse);
	};
};

/** Publish one nurse onto the shift, then the "Staffing - add nurse" event, the snackbar and fresh requests. */
const useSinglePublish = (args: AddStaffSubmitArgs, addNurse: NurseChange["addNurse"]) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { refreshRequests } = useRequestsContext();
	const { weeks, weekIndex, dayNo, shiftNo, room, showActual } = args;

	return async (shift: NurseInShift, picked: Nurse, window: ShiftAvailabilityWindow) => {
		await addNurse({
			nurse: shift,
			...scheduleSlotDay(weeks, { weekIndex, shiftIndex: shiftNo, dayIndex: dayNo }),
			location: room,
			isActual: showActual,
			source: NurseInShiftCreationSource.AddNurse,
		});
		args.setIsAdd(true);
		const date = scheduleSlotDay(weeks, { weekIndex, shiftIndex: shiftNo, dayIndex: dayNo }).date.toFormat("MM/DD/yyyy");
		const target = toAddStaffTarget(args, window);
		track("Staffing - add nurse", buildAddNurseEventProps({ target, date, nurse: picked, selectedCount: SINGLE_ADD_COUNT, batchSize: SINGLE_ADD_COUNT }));
		showSnackbar(t("nurse.added.success", { nurseName: shift.name, day: shift.startTime.toFormat("ccc"), shiftType: t(shift.shiftType) }), "success");
		refreshRequests();
	};
};

/** Publish (or queue in draft mode) one nurse onto the shift and mirror it locally. */
const useSingleAdd = (args: AddStaffSubmitArgs, nurse: Nurse | null, addNurse: NurseChange["addNurse"]) => {
	const { t } = useTranslation();
	const { addNurseToShift } = useScheduleSaves();
	const showErrorSnackbar = useErrorSnackbar();
	const { isDraftMode } = useDraftMode();
	const suggestCloseOpenShift = useCloseOpenShiftSuggestion();
	const redundantOpenShift = useRedundantOpenShift(args, nurse);
	const prepareShift = usePreparedShift(args, nurse);
	const publish = useSinglePublish(args, addNurse);
	const { weeks, weekIndex, dayNo, shiftNo, showActual, hideRoomSelect } = args;

	return async (): Promise<void> => {
		if (!nurse || !args.window) return;
		// Known issue, tracked separately: this path hands the pick to the caller but does NOT return, so the
		// normal publish + local add below still run. Kept as-is on purpose — fixing it changes hospital flows.
		if (hideRoomSelect) {
			// The panel has never had its own date picker, so there is no selected date to hand over.
			args.onNurseSelected?.(nurse, null);
			args.close();
		}
		const shift = prepareShift();
		try {
			if (shift && args.onNurseAddedAvoidActualAdding) {
				args.onNurseAddedAvoidActualAdding(nurse.id);
			} else if (shift) {
				if (!isDraftMode) await publish(shift, nurse, args.window);
				// Draft mode: the local change is the whole add — draft tracking picks it up from hospital data.
				const { date, shiftType } = scheduleSlotDay(weeks, { weekIndex, shiftIndex: shiftNo, dayIndex: dayNo });
				addNurseToShift(shift, date, shiftType, showActual, isDraftMode);
				if (redundantOpenShift) await suggestCloseOpenShift({ request: redundantOpenShift, source: ADD_STAFF_ANALYTICS_SOURCE });
			}
		} catch (e) {
			showErrorSnackbar(t("add.staff.error"), { nurse: shift }, e);
			return;
		}
		args.close();
	};
};
